import { uploadNotificationEmailImage } from "../../action/notification";

export const EMAIL_TEMPLATE_OPTIONS = [
  {
    id: "general-announcement",
    label: "General Announcement",
    subject: "Important update from MirAIe",
    html: `<p>Dear MirAIe User,</p>
<p>We would like to share an important update with you.</p>
<p><strong>[Add your announcement here]</strong></p>
<p>Thank you for being a valued member of the MirAIe community.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "product-update",
    label: "Product Update",
    subject: "New MirAIe product update available",
    html: `<p>Dear MirAIe User,</p>
<p>We are excited to inform you about a new update to your MirAIe experience.</p>
<ul>
  <li><strong>Feature:</strong> [Describe the update]</li>
  <li><strong>Benefit:</strong> [Explain the value to users]</li>
</ul>
<p>Please open the MirAIe app to explore the latest improvements.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "promotion",
    label: "Promotion",
    subject: "Exclusive offer for MirAIe users",
    html: `<p>Dear MirAIe User,</p>
<p>We have an exclusive offer just for you!</p>
<p style="text-align:center;"><strong>[Promotion title]</strong></p>
<p>[Promotion details and terms]</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "maintenance-notice",
    label: "Maintenance Notice",
    subject: "Scheduled MirAIe service maintenance",
    html: `<p>Dear MirAIe User,</p>
<p>Please be informed that scheduled maintenance will be performed on our services.</p>
<p><strong>Date:</strong> [Date]<br/><strong>Time:</strong> [Time window]<br/><strong>Impact:</strong> [Expected impact]</p>
<p>We apologize for any inconvenience and appreciate your understanding.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
];

const NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS = "notification-email-image-thumb";
const NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS = "notification-email-image-item";
const NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS = "notification-email-image-remove";

const isPublicImageUrl = (url = "") => /^https?:\/\/.+/i.test(String(url).trim());

const normalizePublicImageUrl = (url = "") => {
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return trimmed;
};

const notificationImagePreviewUrls = new Map();
let notificationImagePreviewId = 0;

const registerNotificationImagePreviewUrl = (url) => {
  const id = `notification-image-${++notificationImagePreviewId}`;
  notificationImagePreviewUrls.set(id, url);
  return id;
};

export function resetNotificationEmailImageRegistry() {
  notificationImagePreviewUrls.clear();
  notificationImagePreviewId = 0;
}

const getElementAttribute = (element, name) => {
  if (!element) return "";
  if (typeof element.getAttribute === "function") {
    const value = element.getAttribute(name);
    if (value) return value;
  }
  if (element.$ && typeof element.$.getAttribute === "function") {
    return element.$.getAttribute(name) || "";
  }
  return "";
};

const getNotificationImagePreviewUrl = (element) => {
  if (!element) return "";

  const id = getElementAttribute(element, "data-notification-image-id");
  if (id && notificationImagePreviewUrls.has(id)) {
    return notificationImagePreviewUrls.get(id) || "";
  }

  const savedSrc = getElementAttribute(element, "data-notification-image-src");
  const normalizedSaved = normalizePublicImageUrl(savedSrc);
  if (isPublicImageUrl(normalizedSaved)) return normalizedSaved;

  const src = normalizePublicImageUrl(getElementAttribute(element, "src"));
  return isPublicImageUrl(src) ? src : "";
};

// Match editor thumbnail size; table layout keeps dimensions in Gmail/Outlook/mobile clients.
const EMAIL_DELIVERY_IMAGE_MAX_WIDTH = 120;
const EMAIL_DELIVERY_IMAGE_MAX_HEIGHT = 80;

const createEmailDeliveryImage = (src, alt = "") => {
  const w = EMAIL_DELIVERY_IMAGE_MAX_WIDTH;
  const h = EMAIL_DELIVERY_IMAGE_MAX_HEIGHT;
  const normalizedSrc = normalizePublicImageUrl(src) || src;

  const img = document.createElement("img");
  img.setAttribute("src", normalizedSrc);
  img.setAttribute("alt", alt);
  img.setAttribute("width", String(w));
  img.setAttribute("border", "0");
  img.setAttribute(
    "style",
    `display:block;width:${w}px;max-width:${w}px;max-height:${h}px;height:auto;border:0;outline:none;`,
  );

  const link = document.createElement("a");
  link.setAttribute("href", normalizedSrc);
  link.setAttribute("target", "_blank");
  link.setAttribute("rel", "noopener noreferrer");
  link.setAttribute("title", "View full size image");
  link.setAttribute("style", "text-decoration:none;border:0;");
  link.appendChild(img);

  const td = document.createElement("td");
  td.setAttribute("width", String(w));
  td.setAttribute("valign", "top");
  td.setAttribute("style", `width:${w}px;padding:4px;vertical-align:top;`);
  td.appendChild(link);

  const tr = document.createElement("tr");
  tr.appendChild(td);

  const table = document.createElement("table");
  table.setAttribute("role", "presentation");
  table.setAttribute("cellpadding", "0");
  table.setAttribute("cellspacing", "0");
  table.setAttribute("border", "0");
  table.setAttribute("align", "left");
  table.setAttribute("data-email-delivery-image-table", "true");
  table.setAttribute(
    "style",
    "border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;display:inline-table;margin:4px 4px 4px 0;",
  );
  table.appendChild(tr);

  return table;
};

const mergeAdjacentDeliveryImageTables = (container) => {
  const parents = new Set();
  container.querySelectorAll("table[data-email-delivery-image-table]").forEach((table) => {
    if (table.parentElement) parents.add(table.parentElement);
  });

  parents.forEach((parent) => {
    const children = [...parent.childNodes];
    let group = [];

    const flushGroup = () => {
      if (group.length <= 1) {
        group = [];
        return;
      }

      const mergedTable = document.createElement("table");
      mergedTable.setAttribute("role", "presentation");
      mergedTable.setAttribute("cellpadding", "0");
      mergedTable.setAttribute("cellspacing", "0");
      mergedTable.setAttribute("border", "0");
      mergedTable.setAttribute("data-email-delivery-image-table", "true");
      mergedTable.setAttribute(
        "style",
        "border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;margin:4px 0;",
      );

      const tr = document.createElement("tr");
      group.forEach((table) => {
        const td = table.querySelector("td");
        if (td) tr.appendChild(td.cloneNode(true));
      });
      mergedTable.appendChild(tr);
      group[0].replaceWith(mergedTable);
      for (let i = 1; i < group.length; i += 1) {
        group[i].remove();
      }
      group = [];
    };

    children.forEach((node) => {
      if (node.nodeType === 1 && node.matches?.("table[data-email-delivery-image-table]")) {
        group.push(node);
        return;
      }
      flushGroup();
    });
    flushGroup();
  });
};

const cleanupEmptyEmailBlocks = (container) => {
  container.querySelectorAll("p").forEach((paragraph) => {
    const text = paragraph.textContent?.replace(/\u00a0/g, " ").trim();
    if (!text && !paragraph.querySelector("img") && !paragraph.querySelector("table[data-email-delivery-image-table]")) {
      paragraph.remove();
    }
  });
};

const cleanupDeliveryHtml = (container) => {
  container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS}`).forEach((el) => el.remove());
  container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`).forEach((el) => el.remove());
  cleanupEmptyEmailBlocks(container);
};

const openNotificationImagePreview = (url) => {
  const normalized = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalized)) return false;
  window.open(normalized, "_blank", "noopener,noreferrer");
  return true;
};

const focusAfterElement = (editor, element) => {
  if (!editor || !element) return;
  const range = editor.createRange();
  range.setStartAfter(element);
  range.collapse(true);
  editor.getSelection().selectRanges([range]);
};

const createAttachedImagePreview = (editor, url, displayName = "Image") => {
  const normalizedUrl = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalizedUrl)) return null;

  const previewId = registerNotificationImagePreviewUrl(normalizedUrl);
  const img = editor.document.createElement("img");
  img.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS);
  img.setAttribute("src", normalizedUrl);
  img.setAttribute("alt", displayName || "Image");
  img.setAttribute("data-notification-image-id", previewId);
  img.setAttribute("data-notification-image-src", normalizedUrl);
  img.setAttribute("contenteditable", "false");
  img.setAttribute("title", "Click to preview image");
  return img;
};

const createNotificationEmailImageRemoveButton = (editor) => {
  const removeButton = editor.document.createElement("span");
  removeButton.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS);
  removeButton.setAttribute("contenteditable", "false");
  removeButton.setAttribute("title", "Remove image");
  removeButton.setAttribute("role", "button");
  removeButton.setAttribute("aria-label", "Remove image");
  removeButton.setText("\u00d7");
  return removeButton;
};

const createNotificationEmailImageItem = (editor, previewElement) => {
  previewElement.remove();

  const item = editor.document.createElement("span");
  item.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS);
  item.setAttribute("contenteditable", "false");
  item.append(previewElement);
  item.append(createNotificationEmailImageRemoveButton(editor));
  return item;
};

const removeNotificationEmailImageFromDom = (editor, removeButtonEl) => {
  const item = removeButtonEl.closest(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`);
  if (!item) return;

  const preview = item.querySelector(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
  const previewId = preview?.getAttribute("data-notification-image-id");
  if (previewId) {
    notificationImagePreviewUrls.delete(previewId);
  }

  item.remove();
  editor.fire("change");
};

export const insertImageIntoEditor = (editor, url, displayName = "Image") => {
  if (!editor || !url) return;

  const normalizedUrl = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalizedUrl)) return;

  const preview = createAttachedImagePreview(editor, normalizedUrl, displayName);
  if (!preview) return;

  const item = createNotificationEmailImageItem(editor, preview);

  editor.focus();
  editor.insertElement(item);
  focusAfterElement(editor, item);
  editor.fire("change");
};

const countEditorImageThumbs = (editor) => {
  if (!editor?.document) return 0;
  return editor.document.getBody().find(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`).count();
};

const extractEmailContentHtmlFromEditor = (editor) => {
  if (!editor?.document) return "";

  const body = editor.document.getBody();
  const parts = [];
  let child = body.getFirst();

  while (child) {
    parts.push(child.getOuterHtml());
    child = child.getNext();
  }

  return parts.join("").trim();
};

const replaceThumbWithDeliveryImage = (thumb, failed) => {
  const src = getNotificationImagePreviewUrl(thumb);
  const alt = thumb.getAttribute("alt") || "Image";

  if (!isPublicImageUrl(src)) {
    failed.push({
      alt,
      reason: "Attached images could not be read. Upload the image again.",
    });
    return null;
  }

  return createEmailDeliveryImage(src, alt);
};

const unwrapNestedParagraphs = (container) => {
  let nested = container.querySelector("p p");
  while (nested) {
    const inner = nested;
    const parent = inner.parentElement;
    if (parent?.tagName !== "P") break;
    while (inner.firstChild) {
      parent.insertBefore(inner.firstChild, inner);
    }
    inner.remove();
    nested = container.querySelector("p p");
  }
};

const transformEditorImagesForDelivery = (container) => {
  const failed = [];
  let imageCount = 0;

  [...container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`)].forEach((item) => {
    const thumb = item.querySelector(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
    if (!thumb) {
      item.remove();
      return;
    }

    const deliveryNode = replaceThumbWithDeliveryImage(thumb, failed);
    if (!deliveryNode) return;

    imageCount += 1;
    item.replaceWith(deliveryNode);
  });

  [...container.querySelectorAll(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`)].forEach((thumb) => {
    const deliveryNode = replaceThumbWithDeliveryImage(thumb, failed);
    if (!deliveryNode) return;

    imageCount += 1;
    thumb.replaceWith(deliveryNode);
  });

  return { failed, imageCount };
};

export function prepareEmailHtmlForDelivery(editor) {
  if (!editor?.document) {
    return { html: "", failed: [], imageCount: 0 };
  }

  const container = document.createElement("div");
  const contentHtml = extractEmailContentHtmlFromEditor(editor);

  if (contentHtml) {
    container.innerHTML = contentHtml;
  }

  const { failed, imageCount } = transformEditorImagesForDelivery(container);
  mergeAdjacentDeliveryImageTables(container);

  if (countEditorImageThumbs(editor) > 0 && imageCount === 0) {
    return {
      html: "",
      failed: failed.length
        ? failed
        : [{
            alt: "Image",
            reason: "Attached images could not be read. Upload the image again.",
          }],
      imageCount: 0,
    };
  }

  cleanupDeliveryHtml(container);
  unwrapNestedParagraphs(container);

  return {
    html: container.innerHTML.trim(),
    failed,
    imageCount,
  };
}

export const defaultNotificationImageUploadHandler = async (file) => {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Only image files are supported");
  }

  try {
    return await uploadNotificationEmailImage(file);
  } catch (uploadError) {
    const apiMessage = uploadError?.response?.data?.message;
    const status = uploadError?.response?.status;
    const statusHint = status ? ` (HTTP ${status})` : "";
    throw new Error(
      apiMessage || uploadError?.message || `Failed to upload image${statusHint}`
    );
  }
};

let notificationImageMenuOpener = null;

export const bindNotificationImageMenuOpener = (opener) => {
  notificationImageMenuOpener = typeof opener === "function" ? opener : null;
};

const getImageToolbarElement = (editor) => {
  const uiButton = editor?.ui?.instances?.Image;
  if (uiButton?.element) return uiButton.element;
  const root = editor?.container?.$ || editor?.element?.$;
  return root?.querySelector?.(".cke_button__image") || root || null;
};

const openImageInsertMenu = (editor, onOpenMenu) => {
  const opener = onOpenMenu || notificationImageMenuOpener;
  if (typeof opener !== "function") return;
  opener(getImageToolbarElement(editor));
};

const injectNotificationEmailEditorStyles = (editor) => {
  if (!editor?.document || editor._notificationEmailImageStylesAttached) return;
  editor._notificationEmailImageStylesAttached = true;

  const style = editor.document.createElement("style");
  style.setAttribute("type", "text/css");
  style.setAttribute("data-notification-email-styles", "true");
  style.appendText(`
    .${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS} {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      max-width: 140px;
      flex: 0 0 auto;
      position: relative;
      padding-top: 4px;
      padding-right: 4px;
      vertical-align: middle;
      margin: 4px 0;
    }
    img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS} {
      display: block;
      max-width: 120px;
      max-height: 80px;
      width: auto;
      height: auto;
      object-fit: contain;
      border: 1px solid #ed6c02;
      border-radius: 4px;
      background: #ffffff;
      cursor: pointer;
      user-select: none;
    }
    .${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS} {
      position: absolute;
      top: -8px;
      right: -8px;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 1px solid #ed6c02;
      color: #ed6c02;
      background: #ffffff;
      font-size: 14px;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
    }
  `);
  editor.document.getHead().append(style);
};

const attachImageClickHandlers = (editor) => {
  const doc = editor.document?.$;
  if (!doc) return;

  injectNotificationEmailEditorStyles(editor);

  if (doc._notificationEmailClickHandler) {
    doc.removeEventListener("mousedown", doc._notificationEmailClickHandler, true);
  }

  const handleImageActivate = (nativeEvt) => {
    if (nativeEvt.button !== 0) return;

    const removeButton = nativeEvt.target?.closest?.(`.${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS}`);
    if (removeButton) {
      nativeEvt.preventDefault();
      nativeEvt.stopPropagation();
      removeNotificationEmailImageFromDom(editor, removeButton);
      return;
    }

    const previewTarget = nativeEvt.target?.closest?.(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
    if (!previewTarget) return;

    nativeEvt.preventDefault();
    nativeEvt.stopPropagation();
    openNotificationImagePreview(getNotificationImagePreviewUrl(previewTarget));
  };

  doc._notificationEmailClickHandler = handleImageActivate;
  doc.addEventListener("mousedown", handleImageActivate, true);
};

const attachNotificationEmailImageBehavior = (editor) => {
  if (!editor || editor._notificationEmailImageBehaviorAttached) return;
  editor._notificationEmailImageBehaviorAttached = true;

  editor.on("contentDom", () => {
    attachImageClickHandlers(editor);
  });

  attachImageClickHandlers(editor);
};

export const attachCustomImageInsertBehavior = (editor, onOpenMenu) => {
  if (!editor || editor._notificationImageInsertAttached) return;
  editor._notificationImageInsertAttached = true;

  attachNotificationEmailImageBehavior(editor);

  editor.on("beforeCommandExec", (ev) => {
    if (ev.data.name === "link" || ev.data.name === "unlink") {
      ev.cancel();
      return;
    }
    if (ev.data.name !== "image") return;
    ev.cancel();
    openImageInsertMenu(editor, onOpenMenu);
  });

  editor.on("dialogShow", (ev) => {
    const dialogName = ev.data?.getName?.();
    if (dialogName === "image" || dialogName === "link") {
      ev.cancel();
    }
  });
};

export const getNotificationEmailEditorConfig = (isMobile, includeImageInsert = true) => ({
  versionCheck: false,
  extraAllowedContent: [
    `img[!src,alt,style,width,height,class,data-notification-image-id,data-notification-image-src,contenteditable,title](${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS})`,
    "img[!src,alt,style,width,height]",
    `span(!${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS},${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS})`,
  ].join(";"),
  protectedSource: [
    /<img\b[^>]*\bnotification-email-image-thumb\b[^>]*>/gi,
  ],
  toolbar: isMobile
    ? [
        { name: "mobile", items: ["Bold", "Italic", "Underline", "-", "NumberedList", "BulletedList", "-", "JustifyLeft", "JustifyCenter", "JustifyRight"] },
        ...(includeImageInsert ? [{ name: "insert", items: ["Image"] }] : []),
      ]
    : [
        {
          name: "email",
          items: [
            "Undo",
            "Redo",
            "-",
            "Bold",
            "Italic",
            "Underline",
            "Strike",
            "RemoveFormat",
            "-",
            "NumberedList",
            "BulletedList",
            "-",
            "JustifyLeft",
            "JustifyCenter",
            "JustifyRight",
            "-",
            "TextColor",
            "-",
            ...(includeImageInsert ? ["Image", "-"] : []),
            "Source",
          ],
        },
      ],
});
